import {
  Accordion,
  Alert,
  Anchor,
  Button,
  Checkbox,
  Code,
  CopyButton,
  Group,
  Image,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import QRCode from "qrcode";
import { useCallback, useEffect, useRef, useState } from "react";

import { errorMessage, isApiError } from "@/api";

import { useSession } from "../session";
import { ChangePasswordForm } from "./ChangePasswordForm";
import { AuthCard } from "./AuthCard";
import { enableTotp, startTotpSetup } from "./api";

const ISSUER = "Fireslot Back Office";

export const otpauthUrl = (email: string, secret: string) =>
  `otpauth://totp/${encodeURIComponent(ISSUER)}:${encodeURIComponent(email)}?secret=${secret}&issuer=${encodeURIComponent(ISSUER)}`;

type Staged = { secret: string; backup_codes: string[]; qr: string };

/** /2fa/setup: the only screen a setup-only session can reach (spec §3.2). Secrets live in this component's state only. */
export function SetupPage() {
  const { me, refreshMe, logout } = useSession();
  const [staged, setStaged] = useState<Staged | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const started = useRef(false);

  const stage = useCallback(async () => {
    setError(null);
    try {
      const res = await startTotpSetup();
      const qr = await QRCode.toDataURL(otpauthUrl(me?.email ?? "", res.secret), { margin: 1, width: 220 });
      setStaged({ ...res, qr });
      setSaved(false);
    } catch (e) {
      setError(errorMessage(e));
    }
  }, [me?.email]);

  useEffect(() => {
    // Guard against StrictMode's double effect: each setup call stages a new secret.
    if (started.current) return;
    started.current = true;
    void stage();
  }, [stage]);

  const enable = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setCodeError(null);
    try {
      await enableTotp(code.trim());
      setStaged(null);
      await refreshMe(); // → authenticated; SetupOnly redirects to the surface home
    } catch (err) {
      if (isApiError(err, "INVALID_TOTP"))
        setCodeError("That code is not valid. Check the time on your device and try again.");
      else if (isApiError(err, "TOTP_SETUP_EXPIRED")) {
        setCode("");
        setNotice("The setup expired, so a new secret was generated. The QR code changed: scan it again.");
        await stage();
      } else if (isApiError(err, "TOTP_ALREADY_ENABLED")) await refreshMe();
      else setCodeError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthCard
      width={640}
      title="Set up two-factor authentication"
      subtitle="Two-factor authentication is required for every account. Scan the QR code with an authenticator app (Google Authenticator, 1Password, Authy…)."
    >
      <Stack>
        {notice && (
          <Alert color="yellow" data-testid="setup-notice">
            {notice}
          </Alert>
        )}
        {error && (
          <Alert color="red">
            {error}{" "}
            <Anchor component="button" onClick={() => void stage()}>
              Retry
            </Anchor>
          </Alert>
        )}
        {staged && (
          <>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
              <Image
                src={staged.qr}
                alt="Authenticator QR code"
                w={220}
                h={220}
                radius="sm"
                bg="white"
                data-testid="totp-qr"
              />
              <Stack gap="xs">
                <Text size="sm" fw={500}>
                  Can't scan? Enter this key manually:
                </Text>
                <Code block data-testid="totp-secret" style={{ wordBreak: "break-all" }}>
                  {staged.secret}
                </Code>
                <Text size="xs" c="dimmed">
                  Account: {me?.email} · Issuer: {ISSUER}
                </Text>
              </Stack>
            </SimpleGrid>
            <div>
              <Group justify="space-between" mb={6}>
                <Text size="sm" fw={500}>
                  Backup codes (each works once if you lose your device)
                </Text>
                <CopyButton value={staged.backup_codes.join("\n")}>
                  {({ copied, copy }) => (
                    <Button size="compact-xs" variant="light" color={copied ? "teal" : undefined} onClick={copy}>
                      {copied ? "Copied" : "Copy all"}
                    </Button>
                  )}
                </CopyButton>
              </Group>
              <SimpleGrid cols={{ base: 2, sm: 4 }} spacing={6} data-testid="backup-codes">
                {staged.backup_codes.map((c) => (
                  <Code key={c} ta="center">
                    {c}
                  </Code>
                ))}
              </SimpleGrid>
            </div>
            <Checkbox
              label="I have saved these backup codes"
              checked={saved}
              onChange={(e) => setSaved(e.currentTarget.checked)}
              data-testid="backup-saved"
            />
            <form onSubmit={enable}>
              <Group align="flex-start">
                <TextInput
                  placeholder="6-digit code"
                  value={code}
                  onChange={(e) => setCode(e.currentTarget.value)}
                  error={codeError}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  maxLength={6}
                  data-testid="setup-code"
                />
                <Button
                  type="submit"
                  disabled={!saved || code.trim().length !== 6}
                  loading={busy}
                  data-testid="setup-enable"
                >
                  Enable
                </Button>
              </Group>
            </form>
          </>
        )}
        <Accordion variant="separated">
          <Accordion.Item value="pw">
            <Accordion.Control>Change your password (optional, recommended after a first sign-in)</Accordion.Control>
            <Accordion.Panel>
              <ChangePasswordForm />
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
        <Anchor component="button" size="sm" c="dimmed" onClick={() => void logout()}>
          Sign out
        </Anchor>
      </Stack>
    </AuthCard>
  );
}
